// Category B/C ElevenLabs identifiers and public refs.
// Source of truth: this file. Do NOT add these identifiers to vault.
// Classification policy: docs/vault/CLASSIFICATION.md
// Last updated: 2026-05-29 (staging isolation Phase 6)

type ElevenLabsAgentMap = Record<'default' | 'sales' | 'office' | 'customSales' | 'enterprise' | 'welcome', string>;

const DEV_AGENT_ID = 'agent_0601k9de4d3yen9bqssptr1b054n';

const DEV_AGENT_IDS = {
  default: DEV_AGENT_ID,
  sales: DEV_AGENT_ID,
  office: DEV_AGENT_ID,
  customSales: DEV_AGENT_ID,
  enterprise: DEV_AGENT_ID,
  // One EL workspace — the universal welcome agent serves dev/staging too.
  welcome: "agent_6801kvmckq4qf1w9ytv2sy3m9rxf",
} as const satisfies ElevenLabsAgentMap;

export const ELEVENLABS_DEV_AGENT_ID = DEV_AGENT_IDS.default;

const LIVE_AGENT_IDS = {
  default: "agent_5101kbdtbkwne408htvvpr4hh9gx",
  sales: "agent_2701kpyksyc5e1rvm70zee6scjhh",
  office: "agent_2201kpe34mr0fb9bat389n68x0d2",
  customSales: "agent_0701kmk4d74ae2svwtf3q67wbsy8",
  enterprise: "agent_0401kpj9gx5vexda2tn6pfdvwvw9",
  // Universal welcome/onboarding agent for the outbound activation call — the
  // single shared agent that places the "Let's chat" welcome call to every new
  // owner. Created in the live EL workspace 2026-06-21. Override via the
  // WELCOME_ELEVENLABS_AGENT_ID__LIVE env var.
  welcome: "agent_6801kvmckq4qf1w9ytv2sy3m9rxf",
} as const satisfies ElevenLabsAgentMap;

export const ELEVENLABS_LIVE_AGENT_IDS = LIVE_AGENT_IDS;

function env(name: string): string | undefined {
  return process.env[name]?.trim() || undefined;
}

const nextPublicAppUrl = process.env.NEXT_PUBLIC_APP_URL?.trim();
const nextPublicDefaultAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID?.trim() || undefined;
const nextPublicDevAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID_DEV?.trim() || undefined;
const nextPublicCustomSalesAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_CUSTOM_SALES_AGENT_ID?.trim() || undefined;
const nextPublicCustomSalesDevAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_CUSTOM_SALES_AGENT_ID_DEV?.trim() || undefined;
const nextPublicEnterpriseSalesAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_ENTERPRISE_SALES_AGENT_ID?.trim() || undefined;
const nextPublicEnterpriseSalesDevAgentId = process.env.NEXT_PUBLIC_ELEVENLABS_ENTERPRISE_SALES_AGENT_ID_DEV?.trim() || undefined;
const isProductionApp = nextPublicAppUrl === 'https://bizrnr.com';

export const ELEVENLABS_AGENT_IDS = {
  default: isProductionApp
    ? nextPublicDefaultAgentId ?? env('ELEVENLABS_AGENT_ID_LIVE') ?? env('ELEVENLABS_AGENT_ID') ?? LIVE_AGENT_IDS.default
    : env('ELEVENLABS_AGENT_ID_DEV') ?? nextPublicDevAgentId ?? DEV_AGENT_IDS.default,
  sales: isProductionApp
    ? env('BIZRNR_SALES_AGENT_ID_LIVE') ?? env('BIZRNR_SALES_AGENT_ID') ?? LIVE_AGENT_IDS.sales
    : env('BIZRNR_SALES_AGENT_ID_DEV') ?? DEV_AGENT_IDS.sales,
  office: isProductionApp
    ? env('OFFICE_ELEVENLABS_AGENT_ID_LIVE') ?? env('OFFICE_ELEVENLABS_AGENT_ID') ?? LIVE_AGENT_IDS.office
    : env('OFFICE_ELEVENLABS_AGENT_ID_DEV') ?? DEV_AGENT_IDS.office,
  customSales: isProductionApp
    ? nextPublicCustomSalesAgentId ?? env('ELEVENLABS_CUSTOM_SALES_AGENT_ID_LIVE') ?? LIVE_AGENT_IDS.customSales
    : nextPublicCustomSalesDevAgentId ?? env('ELEVENLABS_CUSTOM_SALES_AGENT_ID_DEV') ?? DEV_AGENT_IDS.customSales,
  enterprise: isProductionApp
    ? nextPublicEnterpriseSalesAgentId ?? env('ELEVENLABS_ENTERPRISE_SALES_AGENT_ID_LIVE') ?? LIVE_AGENT_IDS.enterprise
    : nextPublicEnterpriseSalesDevAgentId ?? env('ELEVENLABS_ENTERPRISE_SALES_AGENT_ID_DEV') ?? DEV_AGENT_IDS.enterprise,
  welcome: isProductionApp
    ? env('WELCOME_ELEVENLABS_AGENT_ID__LIVE') ?? env('WELCOME_ELEVENLABS_AGENT_ID') ?? LIVE_AGENT_IDS.welcome
    : env('WELCOME_ELEVENLABS_AGENT_ID__TEST') ?? env('WELCOME_ELEVENLABS_AGENT_ID') ?? DEV_AGENT_IDS.welcome,
} as const satisfies ElevenLabsAgentMap;

export type ElevenLabsAgent = keyof typeof ELEVENLABS_AGENT_IDS;
